import {
  LLMUsageCheckResultStatus,
  STATUSES,
} from '@conversed/rest/src/contracts/llm-usages/check-results';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { iconsHelper } from '../../utils/iconsHelper';

// Selected wins over dimmed; a button is only dimmed while a different status is chosen.
const getActionButtonStateClass = (
  isSelected: boolean,
  isOtherActive: boolean,
): string => {
  if (isSelected) {
    return 'conversation-review-modal__action-button--selected';
  }
  if (isOtherActive) {
    return 'conversation-review-modal__action-button--inactive';
  }
  return '';
};

type Props = {
  currentStatus: LLMUsageCheckResultStatus | '';
  isDisabled: boolean;
  isEvaluable: boolean;
  onStatusChange: (status: LLMUsageCheckResultStatus) => void;
};

export const ConversationReviewActionButtons: React.FC<Props> = ({
  currentStatus,
  isDisabled,
  isEvaluable,
  onStatusChange,
}) => {
  const { t } = useTranslation();

  // A question already handled from another context can only be re-optimized, never
  // re-approved or re-flagged - the verdict lives on the document that was evaluated.
  // Same rule as ConversationCheckStatus in the conversation checklist view.
  const visibleStatuses = Object.entries(STATUSES).filter(
    ([statusKey]) =>
      isEvaluable || statusKey === LLMUsageCheckResultStatus.NEEDS_IMPROVEMENT,
  );

  // "This answer is: Needs improvement" reads as a verdict, but an already-evaluated card
  // carries no verdict of its own - the sibling document holds it. Both the prefix and the
  // single remaining button switch to the action being taken, which is also the wording the
  // confirmation dialog already uses.
  const actionRowLabel = isEvaluable
    ? t('train-ai.conversation-review.action.label')
    : t('train-ai.conversation-review.action.already-reviewed-label');

  return (
    <div className='conversation-review-modal__actions-row'>
      <span className='conversation-review-modal__actions-row-label'>
        {actionRowLabel}
      </span>
      {visibleStatuses.map(
        ([statusKey, { icon, fontColorHex, bgColorRGBA }]) => {
          const status = statusKey as LLMUsageCheckResultStatus;
          const isSelected = currentStatus === status;
          const isOtherActive = Boolean(currentStatus) && !isSelected;
          const label = isEvaluable
            ? t(`train-ai.fact-checking.status.${status}`)
            : t('train-ai.conversation-review.action.reoptimize');
          const stateClass = getActionButtonStateClass(
            isSelected,
            isOtherActive,
          );
          return (
            <button
              key={status}
              type='button'
              disabled={isDisabled}
              style={
                {
                  '--action-button-bg': bgColorRGBA,
                  '--action-button-fg': fontColorHex,
                } as React.CSSProperties
              }
              className={`conversation-review-modal__action-button ${stateClass}`}
              onClick={() => onStatusChange(status)}>
              <img
                src={iconsHelper[icon]}
                alt={label}
                className='conversation-review-modal__action-button-icon'
              />
              {label}
            </button>
          );
        },
      )}
    </div>
  );
};
